/**
 * Deterministic benchmark scenes.
 *
 * Each one pins weather, clock and camera to fixed values so the screenshot harness
 * produces byte-comparable frames run after run, and so visual regressions are real
 * regressions rather than a different wave happening to be under the camera.
 *
 * Reached via ?bench=<name> and driven by tools/screenshot.ts.
 */

import type { RenderWorld } from '../render/world';
import { WEATHER_PRESETS, type WeatherPresetName } from '../sim/weather';
import { DECK_HEIGHT, EYE_X, EYE_Z, WIDE_FOV_DEG, scopeFovDeg } from '../render/constants';
import type { VesselKind } from '../sim/types';
import { TORPEDO_MOUNT } from '../content/weapons';

export interface BenchmarkSpec {
  /** Human description — shown in the generated gallery. */
  description: string;
  weather: WeatherPresetName;
  /** Absolute wave clock, seconds. Chosen so a good swell sits under the camera. */
  time: number;
  /** Camera yaw in degrees (0 = looking down +Z). */
  yaw: number;
  /** Camera pitch in degrees, positive = looking down. */
  pitch: number;
  /** Vertical field of view in degrees. */
  fov: number;
  /** Optional diagnostic camera position; ordinary scenes use the standing eye point. */
  position?: readonly [x: number, y: number, z: number];
  /** Which reference image this frame is meant to be judged against, if any. */
  reference?: string;
  /** Preferred capture aspect. */
  orientation: 'landscape' | 'portrait';
  /** Optional render tier for platform-specific budget and composition proofs. */
  quality?: 'ultra' | 'high' | 'medium' | 'low';
  /** Optional rig integrity for deterministic damage-state lighting checks. */
  rigIntegrity?: number;
  /** Optional one-shot render effect staged after the clock and camera are pinned. */
  effect?:
    | 'torpedoTubeBlast'
    | 'armourRicochet'
    | 'hostileMuzzleFlash'
    | 'rigHitGunfire'
    | 'rigHitCreature';
  /** Exact hostile weapon source used by a launch-flash benchmark. */
  effectSource?: {
    kind: VesselKind;
    bearingDeg: number;
    rangeKm: number;
    headingDeg: number;
  };
  /**
   * Vessels to place before capture. Positions are exact and the sim is settled at the
   * pinned clock, so a ship benchmark is as reproducible as an empty-sea one.
   */
  vessels?: ReadonlyArray<{
    kind: VesselKind;
    bearingDeg: number;
    rangeKm: number;
    /**
     * Absolute heading in degrees, 0 = +Z. Omit to point the vessel at the rig, which is
     * what an inbound ship actually does — but note that bow-on is the least legible
     * angle there is: a 180 m tanker head-on presents 32 m of beam and reads as a buoy.
     * Silhouette checks want a profile.
     */
    headingDeg?: number;
    /** Force the below-surface presentation used by the Kraken's dread/dive phases. */
    submerged?: boolean;
    /** Place the hull part-way through its deterministic settling phase, 0..1. */
    sinkProgress?: number;
  }>;
  /**
   * Pin the Kraken phase machine after the vessels are placed — the finale's rise, stare
   * and fall are continuous poses driven by (phase, timer), not vessel state alone.
   */
  kraken?: { phase: string; timer: number; submergence?: number };
}

export const BENCHMARKS = {
  'ref-wide-landscape': {
    description: 'Wide HORIZON view, overcast sea state 5 — matches the landscape reference framing.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -13,
    pitch: 8,
    fov: WIDE_FOV_DEG,
    reference: 'ref-wide-landscape.png',
    orientation: 'landscape',
  },
  'ref-wide-portrait': {
    description: 'Wide HORIZON view in portrait — more sea below the horizon, per the portrait reference.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -7,
    pitch: 13,
    // Portrait needs a wider vertical field to retain both the platform foreground and
    // the camera-facing tower; the reference itself uses this broader composition.
    fov: 70,
    reference: 'ref-wide-portrait.jpeg',
    orientation: 'portrait',
  },
  'rig-hit-gunfire': {
    description:
      'A port-side round striking the platform — the quick jolt must feel physical '
      + 'without disturbing the player\'s actual aim.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -13,
    pitch: 8,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    effect: 'rigHitGunfire',
  },
  'rig-hit-creature': {
    description:
      'A creature blow striking the platform — the heavier heave must remain readable '
      + 'against the same healthy wide-view composition.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -13,
    pitch: 8,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    effect: 'rigHitCreature',
  },
  'scope-zoom-water': {
    description: 'Water at 8x magnification aimed 4.5km out — proves the sea holds up under the scope.',
    weather: 'overcastDay',
    time: 118.5,
    // Keep the crane outside the narrow scope frustum. The depression includes the
    // 105m eye height and Earth's curvature drop at 4.5km.
    yaw: -30,
    pitch: 1.36,
    fov: scopeFovDeg(8),
    reference: 'ref-scope-landscape.png',
    orientation: 'landscape',
  },
  'scope-zoom-water-portrait': {
    description: 'The same 4.5km water target at 8x in portrait, judged against the portrait scope.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 1.36,
    fov: scopeFovDeg(8),
    reference: 'ref-scope-portrait.jpeg',
    orientation: 'portrait',
  },
  'scope-near-water': {
    description:
      'Water ~60m below the platform at 8x — the worst case for foam texture magnification, '
      + 'where the cellular network can read as diagrammatic polygons.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -18,
    // Just past the platform edge: steeper and the deck itself fills the frame. At 105m
    // eye height this lands on water ~150m out, which under 8x is a couple of dozen
    // metres of sea across the whole frame — well inside one foam texture period.
    pitch: 34,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
  },
  'scope-zoom-water-2x': {
    description: 'Same water at 2x — the honest-magnification comparison against the 8x frame.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 1.36,
    fov: scopeFovDeg(2),
    orientation: 'landscape',
  },
  'ocean-calm': {
    description: 'Sea state 3.5, broken cloud — the low end of the weather range.',
    weather: 'clearDay',
    time: 64,
    yaw: 0,
    pitch: 12,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'storm-heavy': {
    description: 'Sea state 7, severe storm — the worst-case fill-rate and foam load.',
    weather: 'severeStorm',
    time: 240.25,
    yaw: 0,
    pitch: 14,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'ocean-night': {
    description: 'Night, overcast, sea state 4.5 — the dark register tracers must read against.',
    weather: 'night',
    time: 90,
    yaw: 0,
    pitch: 12,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'rig-night-reflection': {
    description: 'Night floodlights rake the wave field beyond the jacket in broken, moving pools.',
    weather: 'night',
    time: 90,
    yaw: -5,
    pitch: 19,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'rig-night-damaged-brownout': {
    description:
      'Critical rig damage — a Crane Tower process deck burns while its lights and sea pools brown out.',
    weather: 'night',
    time: 90.9,
    yaw: -5,
    pitch: 19,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    rigIntegrity: 20,
  },
  'rig-day-damaged-plume': {
    description:
      'Critical damage in overcast daylight — a Crane Tower process fire should feed persistent soot.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 10,
    pitch: 4,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    rigIntegrity: 20,
  },
  'rig-port-damaged-brownout': {
    description:
      'A second critical-damage angle — the port spine should lose its artificial fill as one power system.',
    weather: 'night',
    time: 155,
    yaw: -76,
    pitch: -14,
    fov: 60,
    orientation: 'landscape',
    rigIntegrity: 20,
  },
  'ocean-dusk': {
    description: 'Dusk, warm low light across a building sea.',
    weather: 'dusk',
    time: 155,
    yaw: 30,
    pitch: 10,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'ocean-fog': {
    description: 'Dense fog bank — visibility collapses, the horizon disappears.',
    weather: 'fogBank',
    time: 77,
    yaw: 0,
    pitch: 12,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'ocean-down': {
    description: 'Steep look down at near water — foam, chop detail and swell geometry up close.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 0,
    pitch: 42,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'rig-vertigo': {
    description: 'Looking down over the platform rail at the jacket legs entering the sea far below.',
    weather: 'overcastDay',
    time: 118.5,
    // Step to the rail for this diagnostic. From the ordinary eye position a 58-degree
    // ray intersects the deck before reaching its edge, which made the benchmark judge
    // rust texture rather than the intended thirty-storey drop.
    position: [EYE_X, DECK_HEIGHT, EYE_Z + 0.85],
    yaw: -32,
    pitch: 50,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'crane-tower-understructure': {
    description:
      'Exterior Crane Tower profile — its narrow four-post support must read as one '
      + 'continuous open frame from the crane deck into the sea.',
    weather: 'clearDay',
    time: 64,
    yaw: -237,
    pitch: 12,
    fov: WIDE_FOV_DEG,
    position: [-155, 128, 120],
    orientation: 'landscape',
  },
  'crane-hook': {
    description:
      'The crane hook block and fall, framed close. Boom adjustments otherwise land '
      + 'outside every other benchmark, which makes them impossible to judge.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -10,
    pitch: 10,
    fov: 26,
    orientation: 'landscape',
  },
  'rig-scale': {
    description: 'Looking up and starboard along the derrick — the ~20 storeys above the player.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 34,
    pitch: -12,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'weapon-gun': {
    description:
      'The 30 mm on its raised pad immediately west of the stance — the head-turn the '
      + 'player makes to look at their own gun. Judges whether the mount reads as '
      + 'machinery and sits at the right height beside them.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -97,
    pitch: 4,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'weapon-projector': {
    description:
      'The depth-charge projector on the north-west corner of the crane tower\'s lowest '
      + 'ring gallery, seen from the stance over the rail. Judges whether it reads at '
      + '36 m and whether it sits square on the corner.',
    weather: 'overcastDay',
    time: 118.5,
    // From the stance: the gallery clears the observation rail by a few centimetres at
    // this depression, which is the whole reason Adam chose it over the deck below.
    yaw: 12,
    pitch: 29,
    fov: 50,
    orientation: 'landscape',
  },
  'fx-torpedo-launch': {
    description:
      'Close diagnostic of the low jacket tube — compressed air must spread sideways '
      + 'around the torpedo instead of rising as an ordinary splash cone.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -130,
    pitch: 14,
    fov: 45,
    position: [18, 18, 28],
    orientation: 'landscape',
    effect: 'torpedoTubeBlast',
  },
  'fx-armour-ricochet': {
    description:
      'A visible 30 mm tracer glancing off the corvette — the coherent deflection must '
      + 'separate armoured feedback from an ordinary radial spark hit.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 7.3,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [{ kind: 'corvette', bearingDeg: -30, rangeKm: 0.8, headingDeg: 60 }],
    effect: 'armourRicochet',
  },
  'fx-hostile-salvo': {
    description:
      'A FAC rocket launch at night — the flash must mark the firing hull before the '
      + 'incoming streak reaches the rig.',
    weather: 'night',
    time: 90,
    yaw: -30,
    pitch: 7.2,
    fov: scopeFovDeg(2),
    orientation: 'landscape',
    vessels: [{ kind: 'fac', bearingDeg: -30, rangeKm: 0.8, headingDeg: 60 }],
    effect: 'hostileMuzzleFlash',
    effectSource: { kind: 'fac', bearingDeg: -30, rangeKm: 0.8, headingDeg: 60 },
  },
  'fx-hostile-gunboat': {
    description:
      'A gunboat firing at night — its bow flash must sit on the long forward barrel, '
      + 'not in the middle of the hull.',
    weather: 'night',
    time: 90,
    yaw: -50,
    pitch: 11.5,
    fov: scopeFovDeg(2),
    orientation: 'landscape',
    vessels: [{ kind: 'gunboat', bearingDeg: -50, rangeKm: 0.5, headingDeg: 50 }],
    effect: 'hostileMuzzleFlash',
    effectSource: { kind: 'gunboat', bearingDeg: -50, rangeKm: 0.5, headingDeg: 50 },
  },
  'fx-hostile-corvette': {
    description:
      'A corvette launching at night — the larger flash must rise from its deck cells '
      + 'without swallowing the warship silhouette.',
    weather: 'night',
    time: 90,
    yaw: -70,
    pitch: 6.4,
    fov: scopeFovDeg(2),
    orientation: 'landscape',
    vessels: [{ kind: 'corvette', bearingDeg: -70, rangeKm: 0.9, headingDeg: -20 }],
    effect: 'hostileMuzzleFlash',
    effectSource: { kind: 'corvette', bearingDeg: -70, rangeKm: 0.9, headingDeg: -20 },
  },
  'rig-edge-close': {
    description: 'Close inspection of rectangular housings, beams, slab edges and braces.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 29,
    pitch: 9,
    fov: 38,
    orientation: 'landscape',
  },
  'rig-port-annex': {
    description:
      'Port scrubber annex — verifies the visible left bays are dense while the sea slit stays open.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -100,
    pitch: -17,
    fov: 55,
    orientation: 'landscape',
  },
  'rig-port-ocean-spine': {
    description:
      'Port ocean-edge spine — verifies the protected sea slit is tightly framed by a second occupied mass.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -76,
    pitch: -14,
    fov: 60,
    orientation: 'landscape',
  },
  'rig-port-ocean-spine-dusk': {
    description:
      'Port ocean-edge spine at dusk — verifies functional colour and occupied-floor light rhythm.',
    weather: 'dusk',
    time: 155,
    yaw: -76,
    pitch: -14,
    fov: 60,
    orientation: 'landscape',
  },
  'rig-port-ocean-spine-night': {
    description:
      'Port ocean-edge spine at night — verifies repeated work lights reveal circulation and depth.',
    weather: 'night',
    time: 90,
    yaw: -76,
    pitch: -14,
    fov: 60,
    orientation: 'landscape',
  },
  'rig-process-bay': {
    description:
      'Starboard separator bay — verifies the marked frame reads as a dense process module.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 100,
    pitch: -5,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'rig-process-bay-high': {
    description:
      'Starboard high compression plant — verifies the separator module continues upward.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 100,
    pitch: -18,
    fov: 55,
    orientation: 'landscape',
  },
  'rig-starboard-backbone': {
    description:
      'Starboard accommodation and utility backbone — verifies the process wing has a massive rear body.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 100,
    pitch: -26,
    fov: 55,
    orientation: 'landscape',
  },
  'rig-starboard-outboard': {
    description:
      'Marked right-side gap — verifies the colorful outboard works replace sky beyond the process cage.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 100,
    pitch: -12,
    fov: 60,
    orientation: 'landscape',
  },
  'rig-safety-gear': {
    description:
      'Starboard evacuation station — verifies orange survival craft and launch davits.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 88,
    pitch: 0,
    fov: 38,
    orientation: 'landscape',
  },
  'rig-tower-bridge-void': {
    description:
      'Protected opening above the bridge toward the tower — this volume must remain sky.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 62,
    pitch: -4,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'rig-ocean-corridor': {
    description:
      'Protected sea sightline between the port scrubber train and braced outer frame — '
      + 'new rig mass must frame this slot without crossing it.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -100,
    pitch: 5,
    fov: 38,
    orientation: 'landscape',
  },
  'rig-arc-left-limit': {
    description: 'Far-left free-look limit — verifies the player never turns into an empty set.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -100,
    pitch: 8,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
  'convoy-inbound': {
    description:
      'The core wide-view read: a tanker resolving out of the haze at 3.2 km with escorting '
      + 'hostiles closer in. This is the frame the whole detection model exists to produce.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -22,
    pitch: 6,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    vessels: [
      // Inbound vessels genuinely point at the rig; the escorts are angled across it.
      { kind: 'tanker', bearingDeg: -22, rangeKm: 3.2, headingDeg: 148 },
      { kind: 'skiff', bearingDeg: -30, rangeKm: 1.1, headingDeg: 60 },
      { kind: 'skiff', bearingDeg: -14, rangeKm: 1.5, headingDeg: 200 },
      { kind: 'supply', bearingDeg: -34, rangeKm: 5.4, headingDeg: 140 },
    ],
  },
  'convoy-portrait': {
    description: 'The same convoy in portrait, against the portrait wide reference.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -20,
    pitch: 10,
    fov: 70,
    reference: 'ref-wide-portrait.jpeg',
    orientation: 'portrait',
    vessels: [
      { kind: 'tanker', bearingDeg: -20, rangeKm: 3.0, headingDeg: 150 },
      { kind: 'skiff', bearingDeg: -28, rangeKm: 1.2, headingDeg: 55 },
    ],
  },
  'scope-on-tanker': {
    description:
      'A tanker at 5 km under 8x — the acceptance shot for scope-view water detail and for '
      + 'vessels never showing as billboards under inspection.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -18,
    pitch: 1.1,
    fov: scopeFovDeg(8),
    reference: 'ref-scope-landscape.png',
    orientation: 'landscape',
    vessels: [{ kind: 'tanker', bearingDeg: -18, rangeKm: 5, headingDeg: 68 }],
  },
  'vessel-tanker-close': {
    description:
      'Close 8x three-quarter profile of the tanker — hull sheer, accommodation, '
      + 'lifeboats, deck pipework, materials and pressure wake must all read.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 5.0,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [{ kind: 'tanker', bearingDeg: -30, rangeKm: 1.2, headingDeg: 60 }],
  },
  'vessel-tanker-sinking': {
    description:
      'Mid-settle tanker wreck — the listed hull must displace a broken closing ring '
      + 'instead of descending through undisturbed water.',
    weather: 'clearDay',
    time: 118.5,
    yaw: -30,
    pitch: 5.0,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [{
      kind: 'tanker',
      bearingDeg: -30,
      rangeKm: 1.2,
      headingDeg: 60,
      sinkProgress: 0.55,
    }],
  },
  'vessel-tanker-wreckage': {
    description:
      'A tanker almost fully submerged — large wave-riding fragments and fuel film '
      + 'must carry its scale after the hull has stopped doing so.',
    weather: 'clearDay',
    time: 123.5,
    yaw: -30,
    pitch: 5.0,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [{
      kind: 'tanker',
      bearingDeg: -30,
      rangeKm: 1.2,
      headingDeg: 60,
      sinkProgress: 0.99,
    }],
  },
  'vessel-supply-close': {
    description:
      'Close 8x profile of the supply vessel — forward bridge, working deck, cargo, '
      + 'crane, stern A-frame and wake are judged together.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 7.8,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [{ kind: 'supply', bearingDeg: -30, rangeKm: 0.75, headingDeg: 60 }],
  },
  'vessel-skiff-close': {
    description:
      'Close 8x skiff inspection — open cockpit, unequal crew, twin outboards, '
      + 'improvised weapon and violent small-craft water contact.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 45.3,
    fov: scopeFovDeg(8),
    // Step the inspection camera seaward of the Platform. At this steep angle the
    // player's real firing position looks through the underside of the deck.
    position: [EYE_X - 30, DECK_HEIGHT + 10, EYE_Z + 54],
    orientation: 'landscape',
    vessels: [{ kind: 'skiff', bearingDeg: -30, rangeKm: 0.18, headingDeg: 60 }],
  },
  'vessel-gunboat-close': {
    description:
      'Close 8x gunboat inspection — upright pilothouse, oversized shielded gun, '
      + 'pipe-frame mast and rough modifications must identify the class.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 16.5,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [{ kind: 'gunboat', bearingDeg: -30, rangeKm: 0.35, headingDeg: 60 }],
  },
  'vessel-gunboat-sinking': {
    description:
      'Mid-settle gunboat wreck — the shared closing-water response must remain legible '
      + 'at patrol-craft scale and a steeper viewing angle.',
    weather: 'clearDay',
    time: 118.5,
    yaw: -30,
    pitch: 16.5,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [{
      kind: 'gunboat',
      bearingDeg: -30,
      rangeKm: 0.35,
      headingDeg: 60,
      sinkProgress: 0.55,
    }],
  },
  'vessel-gunboat-oil-slick': {
    description:
      'A gunboat almost fully submerged — scattered panels and its fuel trace must '
      + 'remain readable after the hull and smoke cease to carry the loss.',
    weather: 'clearDay',
    time: 121.5,
    yaw: -30,
    pitch: 16.5,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [{
      kind: 'gunboat',
      bearingDeg: -30,
      rangeKm: 0.35,
      headingDeg: 60,
      sinkProgress: 0.99,
    }],
  },
  'vessel-fac-close': {
    description:
      'Close 8x FAC inspection — low wedge, dark visor, paired missile boxes and swept '
      + 'radar mast must contrast with the pirate gunboat.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 12.2,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [{ kind: 'fac', bearingDeg: -30, rangeKm: 0.48, headingDeg: 60 }],
  },
  'vessel-corvette-close': {
    description:
      'Close 8x corvette profile — gun, sloped citadel, tripod radar mast, funnel, '
      + 'VLS and stern flight deck must form one ordered naval silhouette.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 7.3,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [{ kind: 'corvette', bearingDeg: -30, rangeKm: 0.8, headingDeg: 60 }],
  },
  'vessel-minisub-close': {
    description:
      'Close 8x periscope-depth mini-sub — sail, periscope crook, snorkel, planes and '
      + 'the narrow surface feather are the entire readable threat.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 22.4,
    fov: scopeFovDeg(8),
    position: [EYE_X, DECK_HEIGHT, EYE_Z + 2.5],
    orientation: 'landscape',
    vessels: [{ kind: 'minisub', bearingDeg: -30, rangeKm: 0.25, headingDeg: 60 }],
  },
  'vessel-tentacle-close': {
    description:
      'Close 8x daylight tentacle inspection — hooked S-curve, taper, sucker rows, '
      + 'wet skin and churn at the planted base are visible without night hiding defects.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -30,
    pitch: 21.5,
    fov: scopeFovDeg(8),
    position: [EYE_X, DECK_HEIGHT, EYE_Z + 2.5],
    orientation: 'landscape',
    vessels: [{ kind: 'tentacle', bearingDeg: -30, rangeKm: 0.25, headingDeg: 60 }],
  },
  'vessel-silhouettes': {
    description:
      'Every hull type abeam at matched range — the silhouette-legibility check. Each must '
      + 'be distinguishable from the others as a shape alone.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -13,
    pitch: 3,
    fov: 34,
    orientation: 'landscape',
    vessels: [
      // Beam-on and evenly spaced: this frame exists to compare profiles, so every hull
      // shows its full length.
      // All to port: the rig's own mass fills the starboard half of this framing.
      { kind: 'tanker', bearingDeg: -26, rangeKm: 2.8, headingDeg: 64 },
      { kind: 'corvette', bearingDeg: -16, rangeKm: 2.3, headingDeg: 74 },
      { kind: 'supply', bearingDeg: -8, rangeKm: 1.8, headingDeg: 82 },
      { kind: 'gunboat', bearingDeg: -1, rangeKm: 1.3, headingDeg: 89 },
      { kind: 'skiff', bearingDeg: 4, rangeKm: 0.85, headingDeg: 94 },
    ],
  },
  /**
   * The reveal. The plan's M10 acceptance shot: this has to land the dread, and the
   * silhouette is doing all of the work — the mantle is mostly awash and the waterline
   * is what crops it, because a fully modelled body tells you exactly how big it is.
   */
  'kraken-shadow-10km': {
    description:
      'First contact at 10 km under 8x inspection — only an uncertain kilometre-wide '
      + 'pressure stain and a known-scale tanker disturb the horizon; no body is exposed.',
    weather: 'nightStorm',
    time: 132,
    yaw: -25,
    pitch: 0.8,
    fov: scopeFovDeg(8),
    orientation: 'landscape',
    vessels: [
      {
        kind: 'kraken', bearingDeg: -26, rangeKm: 9.8, headingDeg: 150, submerged: true,
      },
      { kind: 'tanker', bearingDeg: -22, rangeKm: 9.35, headingDeg: 72 },
    ],
  },
  'kraken-shadow-mid': {
    description:
      'Mid-approach daylight proof — the soft mantle mass, warped edge, pressure wake '
      + 'and first partial limb shadows clarify around a tanker without revealing a model.',
    weather: 'overcastDay',
    time: 101,
    yaw: -28,
    pitch: 2,
    fov: 42,
    orientation: 'landscape',
    vessels: [
      {
        kind: 'kraken', bearingDeg: -31, rangeKm: 4.8, headingDeg: 142, submerged: true,
      },
      { kind: 'tanker', bearingDeg: -21, rangeKm: 4.15, headingDeg: 68 },
    ],
  },
  'kraken-shadow-rig': {
    description:
      'Night-storm deadline proof — the close submerged mass and broken pressure foam '
      + 'occupy the same frame as the Platform and a tanker immediately in its path.',
    weather: 'nightStorm',
    time: 136,
    yaw: -29,
    pitch: 9,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    vessels: [
      {
        kind: 'kraken', bearingDeg: -33, rangeKm: 1.12, headingDeg: 146, submerged: true,
      },
      { kind: 'tanker', bearingDeg: -15, rangeKm: 1.28, headingDeg: 70 },
    ],
  },
  'kraken-shadow-close-day': {
    description:
      'Close daylight inspection — a tanker fixes scale over a broad asymmetric shadow, '
      + 'partial limb shapes, lifted water and broken foam while the body remains hidden.',
    weather: 'overcastDay',
    time: 104,
    yaw: -29,
    pitch: 10,
    fov: 58,
    orientation: 'landscape',
    vessels: [
      {
        kind: 'kraken', bearingDeg: -32, rangeKm: 1.06, headingDeg: 142, submerged: true,
      },
      { kind: 'tanker', bearingDeg: -14, rangeKm: 1.22, headingDeg: 68 },
    ],
  },
  'kraken-reveal': {
    description: 'The Kraken surfaced beside the rig at night — the finale reveal.',
    weather: 'nightStorm',
    time: 140,
    yaw: -30,
    pitch: 5,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    vessels: [
      { kind: 'kraken', bearingDeg: -30, rangeKm: 1.60, headingDeg: 122 },
    ],
  },
  'kraken-scale-day': {
    description:
      'Daylight scale proof — the cropped Kraken, a tanker and the Platform coexist '
      + 'in frame, with the animal dwarfing the known hull without exposing its full body.',
    weather: 'overcastDay',
    time: 140,
    yaw: -30,
    pitch: 5,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    vessels: [
      { kind: 'kraken', bearingDeg: -30, rangeKm: 1.60, headingDeg: 122 },
      { kind: 'tanker', bearingDeg: -7, rangeKm: 1.34, headingDeg: 68 },
    ],
  },
  'kraken-anatomy-day': {
    description:
      'Close daylight anatomy proof — linked folds, raised scars, dark wet runnels, the '
      + 'lateral eye and irregular sucker rows must survive inspection beside the Platform.',
    weather: 'overcastDay',
    time: 92,
    yaw: -31,
    pitch: 4,
    fov: 52,
    orientation: 'landscape',
    vessels: [
      { kind: 'kraken', bearingDeg: -31, rangeKm: 0.96, headingDeg: 92 },
      { kind: 'tanker', bearingDeg: -7, rangeKm: 1.08, headingDeg: 68 },
    ],
  },
  'kraken-eye-blink': {
    description:
      'The same close anatomy view during the deterministic heavy blink — the upper '
      + 'lid closes farther than the lower while the tanker preserves scale.',
    weather: 'overcastDay',
    time: 94.15,
    yaw: -31,
    pitch: 4,
    fov: 52,
    orientation: 'landscape',
    vessels: [
      { kind: 'kraken', bearingDeg: -31, rangeKm: 0.96, headingDeg: 92 },
      { kind: 'tanker', bearingDeg: -7, rangeKm: 1.08, headingDeg: 68 },
    ],
  },
  'kraken-mobile-close': {
    description:
      'Medium-tier phone proof — the close Kraken keeps smooth limbs, eye anatomy and '
      + 'readable suckers while the rig uses its phone geometry tier inside the 350k cap.',
    weather: 'overcastDay',
    time: 92,
    yaw: -31,
    pitch: 7,
    fov: 66,
    orientation: 'portrait',
    quality: 'medium',
    vessels: [
      { kind: 'kraken', bearingDeg: -31, rangeKm: 0.96, headingDeg: 92 },
      { kind: 'tanker', bearingDeg: -7, rangeKm: 1.08, headingDeg: 68 },
    ],
  },
  'kraken-motion-coil-a': {
    description:
      'Motion proof A — a tanker fixes scale while the eight main arms carry distinct '
      + 'slow travelling contractions and attached sucker rows.',
    weather: 'overcastDay',
    time: 88,
    yaw: -28,
    pitch: 4,
    fov: 64,
    orientation: 'landscape',
    vessels: [
      { kind: 'kraken', bearingDeg: -36, rangeKm: 1.55, headingDeg: 110 },
      { kind: 'tanker', bearingDeg: -8, rangeKm: 0.92, headingDeg: 60 },
    ],
  },
  'kraken-motion-coil-b': {
    description:
      'Motion proof B — the identical camera twenty-four simulation seconds later; '
      + 'tips lag, curl and overlap differently while the mantle keeps its enormous inertia.',
    weather: 'overcastDay',
    time: 112,
    yaw: -28,
    pitch: 4,
    fov: 64,
    orientation: 'landscape',
    vessels: [
      { kind: 'kraken', bearingDeg: -36, rangeKm: 1.55, headingDeg: 110 },
      { kind: 'tanker', bearingDeg: -8, rangeKm: 0.92, headingDeg: 60 },
    ],
  },
  'kraken-prey-contact': {
    description:
      'Ship-contact staging proof — two unequal limbs rise on opposite sides of a tanker, '
      + 'bend onto the hull and force a visible list inside the Platform combat view.',
    weather: 'overcastDay',
    time: 88,
    yaw: -24,
    pitch: 13,
    fov: 44,
    orientation: 'landscape',
    vessels: [
      { kind: 'tanker', bearingDeg: -24, rangeKm: 0.42, headingDeg: 105 },
      { kind: 'tentacle', bearingDeg: -29, rangeKm: 0.37, headingDeg: 82 },
      { kind: 'tentacle', bearingDeg: -20, rangeKm: 0.47, headingDeg: -104 },
    ],
  },
  'kraken-warship-contact': {
    description:
      'Faction-blind predation proof — two unequal limbs capture an enemy corvette, '
      + 'bend it out of trim and churn a ship-sized displacement field.',
    weather: 'overcastDay',
    time: 101,
    yaw: -18,
    pitch: 14,
    fov: 44,
    orientation: 'landscape',
    vessels: [
      { kind: 'corvette', bearingDeg: -18, rangeKm: 0.38, headingDeg: 110 },
      { kind: 'tentacle', bearingDeg: -23, rangeKm: 0.335, headingDeg: 64 },
      { kind: 'tentacle', bearingDeg: -13, rangeKm: 0.425, headingDeg: -118 },
    ],
  },
  'kraken-rig-wrap-day': {
    description:
      'Exterior daylight rig-contact proof — three independent limbs rise from violent '
      + 'filament boils, shed torn water strands and coil around separate raked jacket legs.',
    weather: 'overcastDay',
    time: 88,
    yaw: 41.5,
    pitch: 17.8,
    fov: 55,
    position: [-155, 125, -175],
    orientation: 'landscape',
    vessels: [
      { kind: 'tentacle', bearingDeg: -128, rangeKm: 0.078, headingDeg: 52 },
      { kind: 'tentacle', bearingDeg: 147, rangeKm: 0.102, headingDeg: -33 },
      { kind: 'tentacle', bearingDeg: -32, rangeKm: 0.091, headingDeg: 148 },
    ],
  },
  /**
   * The shift-10 look: limbs standing in open water, far from anything to seize.
   *
   * Every other tentacle scene captures one gripping something — a hull or a jacket leg —
   * which is the *least* common way the player meets them. A scripted spawn wave puts a
   * group out at one to two kilometres with nothing in reach, and that is the pose Adam
   * was describing as "stiff and wagging but not moving": free limbs, idle motion only.
   *
   * The bearings and ranges reproduce a ring rather than the rank the spawn beat used to
   * fan out, so this also reads whether the group looks like one animal underneath.
   */
  'kraken-limbs-open-water': {
    description:
      'Six free limbs in open water at 0.8–1.1 km — the shift-10 arrival. Judges the '
      + 'travelling slither, the axial roll and whether the group reads as a ring around '
      + 'one body rather than a row of posts.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -28,
    pitch: 6.5,
    fov: 26,
    orientation: 'landscape',
    vessels: [
      { kind: 'tentacle', bearingDeg: -34.0, rangeKm: 0.94, headingDeg: 22 },
      { kind: 'tentacle', bearingDeg: -30.5, rangeKm: 1.06, headingDeg: 141 },
      { kind: 'tentacle', bearingDeg: -27.0, rangeKm: 0.87, headingDeg: -78 },
      { kind: 'tentacle', bearingDeg: -24.0, rangeKm: 1.01, headingDeg: 96 },
      { kind: 'tentacle', bearingDeg: -31.5, rangeKm: 0.82, headingDeg: -142 },
      { kind: 'tentacle', bearingDeg: -26.0, rangeKm: 1.13, headingDeg: 39 },
    ],
  },
  /**
   * The redesigned finale's two staged moments (Adam, 2026-07-30). The assault: limbs
   * reared out of open water in front of the platform, hooked onto the deck edge — the
   * shootable pose, judged from the stance the player actually fights from. The stare:
   * the head up from under the line of sight, the eye level with the deck, looking in.
   */
  'kraken-assault-limbs': {
    description:
      'The final assault from the stance — limbs rooted 108-150 m out rear over the deck '
      + 'edge, upper halves against the sky in the gun\'s pitch band, boil at each breach.',
    weather: 'nightStorm',
    time: 118.5,
    yaw: -30,
    pitch: -2,
    fov: 52,
    orientation: 'landscape',
    vessels: [
      { kind: 'kraken', bearingDeg: -30, rangeKm: 0.47, headingDeg: 60, submerged: true },
      { kind: 'tentacle', bearingDeg: -64, rangeKm: 0.118, headingDeg: 40 },
      { kind: 'tentacle', bearingDeg: -47, rangeKm: 0.148, headingDeg: 152 },
      { kind: 'tentacle', bearingDeg: -30, rangeKm: 0.112, headingDeg: -75 },
      { kind: 'tentacle', bearingDeg: -13, rangeKm: 0.140, headingDeg: 98 },
      { kind: 'tentacle', bearingDeg: 3, rangeKm: 0.121, headingDeg: -140 },
      { kind: 'tentacle', bearingDeg: 19, rangeKm: 0.150, headingDeg: 12 },
    ],
    kraken: { phase: 'tentacles', timer: 30 },
  },
  'kraken-eye-stare': {
    description:
      'The stare — the head risen from under the line of sight, the eye level with the '
      + 'deck at ~130 m, held open, looking straight at the player. The coup-de-grace shot.',
    weather: 'nightStorm',
    time: 118.5,
    yaw: -30,
    pitch: -3,
    fov: 42,
    orientation: 'landscape',
    vessels: [
      { kind: 'kraken', bearingDeg: -30, rangeKm: 0.475, headingDeg: 0 },
    ],
    kraken: { phase: 'rise', timer: 10 },
  },
  /**
   * The dive, caught halfway (Adam, 2026-07-30: it "should actually move down into the
   * water or up from it — not just poof"). Same creature, same instant of the sea, at
   * three points of the descent — the pair that proves it is a mass going under rather
   * than a mesh being switched off.
   */
  'kraken-dive-mid': {
    description:
      'Halfway through the five-and-a-half-second dive — the mantle awash, the crown '
      + 'still out, the body physically passing through the surface rather than fading.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: -31,
    pitch: 3.5,
    fov: 42,
    orientation: 'landscape',
    vessels: [{ kind: 'kraken', bearingDeg: -31, rangeKm: 0.96, headingDeg: 92 }],
    kraken: { phase: 'submerged', timer: 3, submergence: 0.42 },
  },
  'kraken-rig-tear-night': {
    description:
      'Night-storm catastrophe proof — asymmetric sucker-lined limbs load two jacket '
      + 'corners, snap against their upper connections and throw water through the braces.',
    weather: 'nightStorm',
    time: 93.32,
    yaw: -42.5,
    pitch: 17.7,
    fov: 55,
    position: [165, 128, -180],
    orientation: 'landscape',
    vessels: [
      { kind: 'tentacle', bearingDeg: 142, rangeKm: 0.078, headingDeg: -38 },
      { kind: 'tentacle', bearingDeg: -119, rangeKm: 0.103, headingDeg: 61 },
      { kind: 'tentacle', bearingDeg: 31, rangeKm: 0.090, headingDeg: -149 },
    ],
  },

  /** The Maw taking a tanker: first creature contact, shift 10. */
  'maw-assault': {
    description: 'Tentacles alongside a tanker — the Maw encounter.',
    weather: 'nightStorm',
    time: 96,
    yaw: -20,
    pitch: 4,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    vessels: [
      { kind: 'tanker', bearingDeg: -20, rangeKm: 1.1, headingDeg: 150 },
      { kind: 'tentacle', bearingDeg: -23, rangeKm: 1.05, headingDeg: 0 },
      { kind: 'tentacle', bearingDeg: -18, rangeKm: 1.08, headingDeg: 90 },
      { kind: 'tentacle', bearingDeg: -21, rangeKm: 1.14, headingDeg: 200 },
    ],
  },

  'convoy-night': {
    description: 'The convoy at night — the register tracers and floodlights must read against.',
    weather: 'night',
    time: 90,
    yaw: -22,
    pitch: 7,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    vessels: [
      { kind: 'tanker', bearingDeg: -22, rangeKm: 2.4, headingDeg: 150 },
      { kind: 'skiff', bearingDeg: -30, rangeKm: 0.9, headingDeg: 70 },
    ],
  },
  'convoy-storm': {
    description: 'Sea state 7 with vessels in it — hulls must ride the swell, not cut through it.',
    weather: 'severeStorm',
    time: 240.25,
    yaw: -10,
    pitch: 8,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
    vessels: [
      { kind: 'tanker', bearingDeg: -12, rangeKm: 1.8, headingDeg: 74 },
      { kind: 'gunboat', bearingDeg: -2, rangeKm: 0.9, headingDeg: 40 },
    ],
  },
  'rig-arc-right-limit': {
    description: 'Far-right free-look limit — verifies working rig structure across the full arc.',
    weather: 'overcastDay',
    time: 118.5,
    yaw: 100,
    pitch: 8,
    fov: WIDE_FOV_DEG,
    orientation: 'landscape',
  },
} as const satisfies Record<string, BenchmarkSpec>;

export type BenchmarkName = keyof typeof BENCHMARKS;

export function isBenchmarkName(name: string): name is BenchmarkName {
  return Object.prototype.hasOwnProperty.call(BENCHMARKS, name);
}

export function benchmarkNames(): BenchmarkName[] {
  return Object.keys(BENCHMARKS) as BenchmarkName[];
}

interface BenchHost {
  setTime: (t: number) => void;
  /** Set the simulation resource read by damage-state lighting. */
  setRigIntegrity?: (integrity: number) => void;
  /**
   * Set the simulation's weather.
   *
   * The simulation is the weather authority and the render sync pushes its state
   * downstream every frame, so setting the render world alone is not enough — the next
   * sync would simply overwrite it with whatever the loaded shift script specified. That
   * is exactly how a "severe storm" benchmark quietly rendered as an overcast day.
   */
  setSimWeather?: (preset: WeatherPresetName) => void;
  /**
   * Place a vessel and settle it on the pinned wave field. Absent when the harness boots
   * without a simulation, in which case vessel manifests are simply skipped.
   */
  placeVessel?: (
    kind: VesselKind, bearingDeg: number, rangeKm: number, headingDeg?: number,
    submerged?: boolean, sinkProgress?: number,
  ) => void;
  /**
   * Pin the Kraken encounter's phase machine, so the finale's staged poses — the rise,
   * the stare, the fall — can be captured deterministically.
   */
  setKrakenPhase?: (phase: string, timer: number, submergence?: number) => void;
}

/** Apply a benchmark: fixed weather, fixed clock, fixed camera. */
export function applyBenchmark(world: RenderWorld, host: BenchHost, name: BenchmarkName): void {
  const spec: BenchmarkSpec = BENCHMARKS[name];

  host.setSimWeather?.(spec.weather);
  host.setRigIntegrity?.(spec.rigIntegrity ?? 100);
  world.setWeather(WEATHER_PRESETS[spec.weather]);
  // The default vantage is wherever the player actually stands.
  const position = spec.position ?? [EYE_X, DECK_HEIGHT, EYE_Z];
  world.camera.position.set(position[0], position[1], position[2]);
  world.camera.rotation.set((spec.pitch * Math.PI) / 180, (spec.yaw * Math.PI) / 180, 0);
  world.camera.fov = (spec.fov * Math.PI) / 180;

  // Vessels are placed before the clock is pinned so they settle onto the exact wave
  // field the frame will render.
  if (spec.vessels && host.placeVessel) {
    for (const v of spec.vessels) {
      host.placeVessel(
        v.kind,
        v.bearingDeg,
        v.rangeKm,
        v.headingDeg,
        v.submerged,
        v.sinkProgress,
      );
    }
  }

  // Phase state after vessels exist, so the machine has its boss to pose.
  if (spec.kraken) {
    host.setKrakenPhase?.(spec.kraken.phase, spec.kraken.timer, spec.kraken.submergence);
  }

  // Freeze last so the pinned clock survives.
  host.setTime(spec.time);

  if (spec.effect === 'torpedoTubeBlast') {
    world.effects.tubeBlast(
      TORPEDO_MOUNT.x,
      TORPEDO_MOUNT.y,
      TORPEDO_MOUNT.z,
      0,
      1,
    );
  } else if (spec.effect === 'armourRicochet') {
    const bearing = (-30 * Math.PI) / 180;
    world.effects.hullImpact(
      Math.sin(bearing) * 800,
      9,
      Math.cos(bearing) * 800,
      true,
      1,
    );
  } else if (spec.effect === 'hostileMuzzleFlash') {
    if (!spec.effectSource) return;
    const bearing = (spec.effectSource.bearingDeg * Math.PI) / 180;
    const range = spec.effectSource.rangeKm * 1000;
    world.effects.hostileMuzzleFlash(
      Math.sin(bearing) * range,
      2.5,
      Math.cos(bearing) * range,
      -Math.sin(bearing),
      0.1,
      -Math.cos(bearing),
      spec.effectSource.kind,
      (spec.effectSource.headingDeg * Math.PI) / 180,
    );
  } else if (spec.effect === 'rigHitGunfire') {
    world.triggerRigImpact(-900, 1100, 12, 'gunfire', spec.time - 0.07);
    world.updateRigImpactCamera(spec.time);
    world.pushFrame();
  } else if (spec.effect === 'rigHitCreature') {
    world.triggerRigImpact(180, 120, 24, 'creature', spec.time - 0.15);
    world.updateRigImpactCamera(spec.time);
    world.pushFrame();
  }
}
